import React from 'react';

import BrellaWidget from './BrellaWidget';

// Brella widgets are authored as a plain-text marker. Plain text is the only thing that
// survives the CMS untouched: CKEditor's ACF strips `id`/`style` attributes and
// react-html-parser drops every <script> node, so the real Brella markup is synthesized by
// BrellaWidget rather than stored.
//
//   [brella:{ "joinCode": "JoinCode", "widgetType": "speakers" }]
//
// The object is Brella's own props block, pasted verbatim from their embed snippet, and is
// merged over the per-type defaults.
//
// This lives apart from utils/reactHtmlParserTransform so the events pages do not pull in
// that module's article/newsletter dependency graph.
const MARKER_START = '[brella:';

// The CMS runs authored text through RubyPants, which turns straight quotes curly. Undo
// that before parsing.
const parseProps = (source: string): Record<string, unknown> | null => {
  try {
    const parsed = JSON.parse(source.replace(/[“”]/g, '"').replace(/[‘’]/g, "'"));
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : null;
  } catch {
    return null;
  }
};

// A marker can't be terminated by a plain regex: its payload is JSON, which contains both
// `]` (arrays) and `}`. Walk the text tracking nesting and string state, and stop at the
// first `]` that closes the marker itself. Returns the index of that `]`, or -1.
const findMarkerEnd = (data: string, start: number): number => {
  let depth = 0;
  let inString = false;
  let escaped = false;

  for (let i = start; i < data.length; i += 1) {
    const ch = data[i];

    if (inString) {
      if (escaped) escaped = false;
      else if (ch === '\\') escaped = true;
      else if (ch === '"' || ch === '“' || ch === '”') inString = false;
      continue;
    }

    if (ch === '"' || ch === '“' || ch === '”') inString = true;
    else if (ch === '{' || ch === '[') depth += 1;
    else if (ch === '}') depth -= 1;
    else if (ch === ']') {
      if (depth === 0) return i;
      depth -= 1;
    }
  }
  return -1;
};

interface ParsedMarker {
  widgetType: string;
  joinCode: string;
  props: Record<string, unknown>;
}

const parseMarker = (inner: string): ParsedMarker | null => {
  const props = parseProps(inner.trim());
  const widgetType = props?.widgetType;
  const joinCode = props?.joinCode;
  if (typeof widgetType !== 'string' || typeof joinCode !== 'string') return null;
  return { widgetType, joinCode, props: props as Record<string, unknown> };
};

// Splits a text node into the copy around each marker and a widget for each one. Returns
// null when the node holds no marker at all, so the caller can fall through untouched.
const splitOnBrellaMarkers = (data: string, keyPrefix: string) => {
  if (!data.includes(MARKER_START)) return null;

  const out: (string | JSX.Element)[] = [];
  let cursor = 0;
  let found = false;

  for (;;) {
    const start = data.indexOf(MARKER_START, cursor);
    if (start === -1) break;

    const end = findMarkerEnd(data, start + MARKER_START.length);
    if (end === -1) break;

    const parsed = parseMarker(data.slice(start + MARKER_START.length, end));
    if (parsed) {
      found = true;
      if (start > cursor) out.push(data.slice(cursor, start));
      out.push(
        <BrellaWidget
          key={`${keyPrefix}-${start}`}
          widgetType={parsed.widgetType}
          joinCode={parsed.joinCode}
          overrides={parsed.props}
        />
      );
      cursor = end + 1;
    } else {
      // Malformed: leave it as literal text so the mistake is visible on the page.
      out.push(data.slice(cursor, end + 1));
      cursor = end + 1;
    }
  }

  if (!found) return null;
  if (cursor < data.length) out.push(data.slice(cursor));
  return out;
};

// Returning undefined lets react-html-parser fall through to its standard handling; it
// propagates this transform to children on its own. Anything but a marker is untouched.
const eventTransform = (node, index) => {
  if (node.type === 'text') {
    const rendered = splitOnBrellaMarkers(node.data ?? '', `brella-${index}`);
    if (rendered) {
      return <React.Fragment key={`brella-${index}`}>{rendered}</React.Fragment>;
    }
  }
  return undefined;
};

// Landing-page cards show a description excerpt; a widget must never mount there.
const stripBrellaMarkers = (text: string) => {
  let out = text || '';
  for (;;) {
    const start = out.indexOf(MARKER_START);
    if (start === -1) return out;
    const end = findMarkerEnd(out, start + MARKER_START.length);
    if (end === -1) return out;
    out = out.slice(0, start) + out.slice(end + 1);
  }
};

export { eventTransform, stripBrellaMarkers };
